import { Checkbox, Modal } from '@mantine/core'
import { IconUserX, IconX } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { CANCEL_BTN, MODAL_CONTENT, SUBMIT_BTN } from '@/components/ui/formModal.tokens'

export interface NoShowConfirmModalProps {
  opened: boolean
  /** Zocdoc's own appointment id, when this visit is bound to a booking there. */
  zocdocAppointmentId?: string
  /**
   * The visit's start, to say up front when Zocdoc will refuse the no-show: they take one only
   * after the start has passed and while it is under two days old.
   */
  startAt?: string
  /** True while the status change is in flight. */
  saving?: boolean
  onClose: () => void
  onConfirm: (options: { pushToZocdoc: boolean }) => void
}

/** Zocdoc's window for a no-show: after the start, and within two days of it. */
const NO_SHOW_MAX_AGE_MS = 2 * 24 * 60 * 60 * 1000

/**
 * Confirmation in front of marking a patient as a no-show.
 *
 * The status dropdown is otherwise a one-click change, and this is the one value on it that
 * reaches outside beHuman — so the Zocdoc half is offered here rather than happening silently.
 * Mirrors the Schedule page's cancel dialog, which asks the same question about the same booking.
 */
export const NoShowConfirmModal = ({
  opened,
  zocdocAppointmentId,
  startAt,
  saving = false,
  onClose,
  onConfirm,
}: NoShowConfirmModalProps) => {
  const [pushToZocdoc, setPushToZocdoc] = useState(true)
  // Re-armed per opening, so someone who unticked it once does not carry that into the next
  // patient.
  useEffect(() => {
    if (opened) setPushToZocdoc(true)
  }, [opened])

  // Worked out here rather than left to the refusal, so the operator learns it before spending
  // the click — the request would come back rejected either way.
  const startedAt = startAt ? Date.parse(startAt) : NaN
  const age = Number.isFinite(startedAt) ? Date.now() - startedAt : undefined
  const zocdocWillRefuse =
    age === undefined
      ? undefined
      : age < 0
        ? 'not-started'
        : age > NO_SHOW_MAX_AGE_MS
          ? 'too-old'
          : undefined

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      withCloseButton={false}
      classNames={{ content: MODAL_CONTENT, body: '!p-0' }}
      radius={18}
      size="440px"
      // Above the Schedule's appointment preview popover, which stays open behind this dialog and
      // otherwise wins on z-index (Mantine: popover 300, modal 200) — as the cancel dialog does.
      zIndex={400}
    >
      <div className="flex flex-col gap-4 px-6 pt-5 pb-6">
        <div className="flex items-start justify-between">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FDECE8] text-[#E5533D]">
            <IconUserX size={20} stroke={2} />
          </span>
          <button
            type="button"
            title="Close"
            aria-label="Close"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-[7px] text-[#8A929C] hover:bg-[#F2F4F6] hover:text-[#1F2733]"
          >
            <IconX size={17} stroke={2.1} />
          </button>
        </div>

        <div>
          <h2 className="m-0 text-[22px] font-extrabold tracking-[-0.4px] text-[#1F2733]">
            Mark as no-show?
          </h2>
          <p className="mt-1.5 mb-0 text-[13.5px] text-[#6B7280]">
            This records that the patient did not attend.
          </p>
        </div>

        {zocdocAppointmentId && (
          <Checkbox
            checked={pushToZocdoc}
            onChange={(event) => setPushToZocdoc(event.currentTarget.checked)}
            disabled={saving || Boolean(zocdocWillRefuse)}
            label="Also record the no-show on Zocdoc"
            description={
              zocdocWillRefuse === 'not-started'
                ? 'Zocdoc accepts a no-show only after the appointment’s start time.'
                : zocdocWillRefuse === 'too-old'
                  ? 'Zocdoc accepts a no-show only within two days of the start, so this one can no longer be sent.'
                  : pushToZocdoc
                    ? 'This visit came from Zocdoc. The no-show will be recorded there too.'
                    : 'Only the beHuman visit will be marked — Zocdoc will still show the patient as expected.'
            }
            classNames={{ label: 'text-[13.5px] font-semibold text-[#1F2733]' }}
          />
        )}

        <div className="grid grid-cols-2 gap-3">
          <button type="button" className={CANCEL_BTN} onClick={onClose} disabled={saving}>
            Keep current status
          </button>
          <button
            type="button"
            className={SUBMIT_BTN}
            onClick={() =>
              onConfirm({
                pushToZocdoc: Boolean(zocdocAppointmentId) && pushToZocdoc && !zocdocWillRefuse,
              })
            }
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Mark as no-show'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
